import { z } from 'zod';
import { uuidSchema } from './common.js';
import { calculationBasisSchema } from './financials.js';
import { SETUP_STEP_KEYS, SETUP_TOTAL_STEPS } from '../financials-setup.js';

/**
 * Wire DTOs for `GET /api/client/financials/setup` +
 * `POST .../confirm-policy` + `POST .../mark-reviewed` (SIL-293, W2 of
 * resources/plans/financials-hifi-flow-spec.md slice 1 "Setup Guide").
 *
 * Bare-JSON success responses (no `{data:...}` envelope), matching
 * financials.ts / fmv-outlook.ts. Errors use the shared `apiErrorSchema`.
 */

// `SetupStepKey` / `SetupStepState` types are NOT re-exported here — the
// canonical definitions live in `../financials-setup.js` (the pure kernel);
// these schemas only validate that shape at the wire boundary, so a second,
// identically-named type export here would just collide (TS2308) without
// adding information.
export const setupStepKeySchema = z.enum(SETUP_STEP_KEYS);

export const setupStepStateSchema = z.object({
  step: z.number().int().min(1).max(SETUP_TOTAL_STEPS),
  key: setupStepKeySchema,
  complete: z.boolean(),
  /** Step-3-only — see `stepStates` in `financials-setup.ts`. Optional so
   * the other five steps' wire shape stays uncluttered. */
  reopened: z.boolean().optional(),
});

/**
 * Readiness meter — spec: "self-asserted (members can trivially complete
 * steps 5/6) — it must never appear in an export or as an assurance
 * artifact. Derived per request under the caller's GUC; never edge-cached."
 * Every field here is a coverage PERCENT (0-100), never stored.
 */
export const setupReadinessSchema = z.object({
  score: z.number().min(0).max(100),
  pctAssetsWithFinancials: z.number().min(0).max(100),
  pctLiveTelemetry: z.number().min(0).max(100),
  compsFreshnessPct: z.number().min(0).max(100),
});
export type SetupReadiness = z.infer<typeof setupReadinessSchema>;

/** GET /api/client/financials/setup response. */
export const financialsSetupResponseSchema = z.object({
  steps: z.array(setupStepStateSchema).length(SETUP_TOTAL_STEPS),
  completedCount: z.number().int().min(0).max(SETUP_TOTAL_STEPS),
  totalSteps: z.literal(SETUP_TOTAL_STEPS),
  readiness: setupReadinessSchema,
  /** Level-2 basis disclosure for `readiness` only — the six `steps` are
   * literal booleans/timestamps, not computed figures needing disclosure
   * (spec Cross-cutting). Optional per the retrofit-is-additive rule. */
  basis: calculationBasisSchema.optional(),
  generatedAt: z.string(),
});
export type FinancialsSetupResponse = z.infer<typeof financialsSetupResponseSchema>;

/** POST /api/client/financials/setup/confirm-policy response (admin-only). */
export const confirmPolicyResponseSchema = z.object({
  policyConfirmedAt: z.string(),
  policyConfirmedBy: uuidSchema,
});
export type ConfirmPolicyResponse = z.infer<typeof confirmPolicyResponseSchema>;

/** POST /api/client/financials/setup/mark-reviewed response (any member). */
export const markReviewedResponseSchema = z.object({
  bookVsFmvViewedAt: z.string(),
  bookVsFmvViewedBy: uuidSchema,
});
export type MarkReviewedResponse = z.infer<typeof markReviewedResponseSchema>;
